import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AddOnTextInput, Button, DropdownSelect, Dropzone, Icon, Spinner, TextInput, Toggle, Tooltip, Typography } from "@/components/atoms";
import { useGetVehicleTypesQuery } from "@/redux/apis/config/trip/vehicleType/vehicleTypeApi";
import { VeivoPrefill } from "@/redux/apis/vehicle/veivoApi";
import { useGetVehicleFormOptionsQuery } from "@/redux/apis/vehicle/vehicleApi";
import AvatarPlaceholder from "@/assets/images/vehicle/avatar-placeholder.svg";
import { VehicleTypeMultiSelect } from "../../VehicleTypeMultiSelect";
import { V3DetailsDraft } from "../steps";

const detailsSchema = z.object({
  vehicleTypeUuids: z.array(z.string()).min(1, "Please select at least one vehicle type"),
  nickname: z.string(),
  hasMarkings: z.boolean(),
  capacity: z.number().min(1, "Please select passenger capacity"),
  bags: z.number().min(0, "Please select bag capacity"),
  maintenanceFee: z.number().min(0).max(100),
});

/** Without the registry's seat count, the plate path's ceiling. */
const DEFAULT_MAX_PAX = 14;

/**
 * Step 3 (DEV-1903): what RideMinder needs that a verification cannot know —
 * the plate path's own field set, pre-filled from what it can (the seat count
 * suggests the capacity). The photo is the walk-around's front still; the
 * operator may choose another, uploaded once the vehicle exists.
 *
 * Kept as draft data, not a vehicle: nothing is a vehicle until Review.
 */
export const DetailsStep = ({
  prefill,
  saved,
  photoUrl,
  onPhoto,
  onBack,
  onNext,
  onFinishLater,
  saving,
}: {
  prefill: VeivoPrefill | undefined;
  saved: V3DetailsDraft | undefined;
  /** The operator's choice, or the walk-around's front still. */
  photoUrl: string | null;
  onPhoto: (file: File) => void;
  onBack: () => void;
  onNext: (details: V3DetailsDraft) => void;
  onFinishLater: (details: V3DetailsDraft) => void;
  saving: boolean;
}) => {
  const { data: options, isFetching: fetchingOptions } = useGetVehicleFormOptionsQuery();
  const { data: typeStatus } = useGetVehicleTypesQuery({ page: "1", page_size: "200" });

  const vehicleTypes = useMemo(() => {
    const activeByUuid = new Map(typeStatus?.vehicleTypes.map((t) => [t.uuid, t.isActive]));
    return (options?.vehicleTypes ?? []).map((vt) => ({ name: vt.name, value: vt.uuid, isActive: activeByUuid.get(vt.uuid) }));
  }, [options?.vehicleTypes, typeStatus?.vehicleTypes]);

  const suggested = prefill?.suggestedCapacityPax ?? null;
  // Web-app seats the model with the registry's count and refuses more.
  const maxPax = prefill?.seats ? Math.max(prefill.seats - 1, 1) : DEFAULT_MAX_PAX;

  const {
    control,
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<V3DetailsDraft>({
    resolver: zodResolver(detailsSchema),
    defaultValues: saved ?? {
      vehicleTypeUuids: [],
      nickname: "",
      hasMarkings: false,
      capacity: suggested ?? 0,
      bags: 0,
      maintenanceFee: 0,
    },
  });

  return (
    <form onSubmit={handleSubmit(onNext)} className="flex flex-col gap-6">
      <Dropzone
        accept={{ "image/png": [".png"], "image/jpeg": [".jpeg", ".jpg"] }}
        maxSize={10000000}
        upload={onPhoto}
        className="w-full border-transparent p-5 transition-colors hover:border-neutral-dark-gray"
        detail={
          <div className="flex flex-col items-start gap-6 md:flex-row md:items-center">
            <img src={photoUrl ?? AvatarPlaceholder} alt="Vehicle" className="h-[96px] w-[96px] shrink-0 rounded-lg object-cover" />
            <div className="flex flex-1 flex-col">
              <Typography variant="action" className="text-black">
                Vehicle Image
              </Typography>
              <Typography className="mt-2 text-neutral-dark-gray">
                {photoUrl
                  ? "We've used the front photo from your walk-around. You can change it to any PNG or JPG."
                  : "Upload a vehicle image (PNG or JPG) — a front view with the number plate on it."}
              </Typography>
              <Button type="button" variant="custom" size="sm" className="mt-0.5 -translate-x-3 self-start text-info">
                Change photo
              </Button>
            </div>
          </div>
        }
      />

      <div className="flex items-center gap-3">
        <Typography>Vehicle has markings?</Typography>
        <Controller
          control={control}
          name="hasMarkings"
          render={({ field }) => <Toggle size="sm" checked={field.value} onChange={field.onChange} />}
        />
      </div>

      <div className="grid grid-cols-2 gap-5 gap-y-6">
        <label htmlFor="vehicleTypeUuids" className="col-span-2 xl:col-span-1">
          <Typography className="relative flex items-center gap-2 leading-[2rem]">
            Vehicle Type
            <Tooltip content="The type classification of your vehicle" placement="top">
              <Icon name="InfoCircle" variant="Bold" size="md" className="relative z-10 rotate-180 text-neutral-dark-gray" />
            </Tooltip>
          </Typography>
          <Controller
            control={control}
            name="vehicleTypeUuids"
            render={({ field }) => (
              <VehicleTypeMultiSelect
                options={vehicleTypes}
                value={field.value}
                onChange={field.onChange}
                hasError={!!errors.vehicleTypeUuids}
                disabled={fetchingOptions}
              />
            )}
          />
          {errors.vehicleTypeUuids && <Typography className="mt-1 text-sm text-danger">{errors.vehicleTypeUuids.message}</Typography>}
        </label>

        <label htmlFor="nickname" className="col-span-2 xl:col-span-1">
          <Typography className="leading-[2rem]">Vehicle Nickname (Optional)</Typography>
          <TextInput {...register("nickname")} id="nickname" placeholder="Enter Vehicle Nickname" className="[&>div]:w-full [&_input]:w-full" />
        </label>

        <label htmlFor="capacity" className="col-span-2 xl:col-span-1">
          <Typography className="leading-[2rem]">Passenger Capacity</Typography>
          <Controller
            control={control}
            name="capacity"
            render={({ field }) => (
              <DropdownSelect
                className="w-full"
                placeholder="Select Passenger Capacity"
                options={Array.from({ length: maxPax }, (_, i) => (i + 1).toString())}
                value={field.value ? field.value.toString() : undefined}
                onChange={(value: string) => field.onChange(+value)}
              />
            )}
          />
          {errors.capacity ? (
            <Typography className="mt-1 text-sm text-danger">{errors.capacity.message}</Typography>
          ) : (
            suggested && (
              <Typography variant="paragraph" className="mt-1 text-neutral-silver-gray">
                Suggested from the registration — change it if needed
              </Typography>
            )
          )}
        </label>

        <label htmlFor="bags" className="col-span-2 xl:col-span-1">
          <Typography className="leading-[2rem]">Bag Capacity</Typography>
          <Controller
            control={control}
            name="bags"
            render={({ field }) => (
              <DropdownSelect
                className="w-full"
                placeholder="Select Number of Capacity"
                options={Array.from({ length: 101 }, (_, i) => i.toString())}
                value={field.value?.toString()}
                onChange={(value: string) => field.onChange(+value)}
              />
            )}
          />
        </label>

        <label htmlFor="maintenanceFee" className="col-span-2 xl:col-span-1">
          <Typography className="leading-[2rem]">Vehicle Owner Maintenance Fee (Optional)</Typography>
          <AddOnTextInput
            {...register("maintenanceFee", { valueAsNumber: true })}
            id="maintenanceFee"
            type="number"
            endAddOn="%"
            min={0}
            max={100}
            placeholder="0"
            className="w-full [&>div]:flex-1"
          />
          <Typography variant="paragraph" className="mt-1 text-neutral-silver-gray">
            Portion of the driver payment paid directly to the owner of the vehicle
          </Typography>
        </label>
      </div>

      <div className="mt-4 flex flex-col-reverse gap-3 xl:flex-row xl:justify-between xl:gap-4">
        <Button
          type="button"
          variant="custom"
          size="xs"
          className="text-neutral-silver-gray underline xl:w-auto"
          disabled={saving}
          onClick={() => onFinishLater(getValues())}
        >
          {saving ? <Spinner /> : "Finish later"}
        </Button>
        <div className="flex flex-col-reverse gap-3 xl:flex-row xl:gap-4">
          <Button type="button" variant="secondary" size="lg" className="w-full xl:w-auto" startIcon="ArrowLeft" onClick={onBack}>
            Back
          </Button>
          <Button type="submit" variant="primary" size="lg" className="w-full xl:w-auto" endIcon="ArrowRight">
            Next
          </Button>
        </div>
      </div>
    </form>
  );
};
